<template>
  <div class="template-builder">
    <div class="d-flex align-items-center header">
      <div class="flex-fill header-question">{{ $t("maintenance.question") }}</div>
      <div class="header-type-response">{{ $t("maintenance.type_response") }}</div>
    </div>

    <div v-if="moveMode" class="move-toolbar d-flex align-items-center justify-content-between">
      <div class="small">
        {{ $t("maintenance.moving") }}
        <strong>{{ movingItemLabel }}</strong>
      </div>

      <div class="d-flex align-items-center">
        <button
          type="button"
          class="btn btn-sm btn-outline-primary mr-1"
          @click="moveSelectedToTop"
        >
          {{ $t("maintenance.move_to_top") }}
        </button>

        <button
          type="button"
          class="btn btn-sm btn-outline-primary mr-1"
          @click="moveSelectedToBottom"
        >
          {{ $t("maintenance.move_to_bottom") }}
        </button>

        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="cancelMove"
        >
          {{ $t("maintenance.cancel") }}
        </button>
      </div>
    </div>

    <draggable v-model="itemsArray" handle=".handle" @update="onUpdate">
      <template-field
        v-for="(item, index) in itemsArray"
        v-show="item.type !== 'facility'"
        :key="item.item_id"
        :data="item"
        :selected="itemSelected.item_id === item.item_id"
        :section-fields="visibleFields"
        :index-in-section="getVisibleIndex(item)"
        :move-mode="moveMode"
        :is-moving-source="movingItemId === item.item_id"
        :can-move-here="canMoveHere(item)"
        @item-selected="onItemSelected"
        @show-modal="showModal"
        @remove="onRemove(index)"
        @start-move="startMove"
        @cancel-move="cancelMove"
        @move-before="moveBefore"
        @move-after="moveAfter"
      />
    </draggable>

    <template-field-types-modal
      :key="itemSelected.item_id"
      :type.sync="itemSelected.type"
      :field-type-cfg.sync="itemSelected.options.fieldTypeCfg"
      @modal-close="onModalClose"
    />
  </div>
</template>

<script>
import draggable from "vuedraggable";
import TemplateField from "./TemplateField.vue";
import TemplateFieldTypesModal from "./TemplateFieldTypesModal.vue";
import { fieldStructure } from "./config";

export default {
  props: {
    items: Array,
    isValid: Boolean,
  },
  components: {
    draggable,
    TemplateField,
    TemplateFieldTypesModal,
  },
  data() {
    return {
      itemsArray: [],
      itemSelected: { ...fieldStructure },
      movingItemId: null,
    };
  },
  computed: {
    visibleFields() {
      return (this.itemsArray || []).filter((i) => i.type !== "facility");
    },
    moveMode() {
      return !!this.movingItemId;
    },
    movingItem() {
      return (this.itemsArray || []).find((i) => i.item_id === this.movingItemId) || null;
    },
    movingItemLabel() {
      if (!this.movingItem) return this.$t("maintenance.unknown_field");
      return this.movingItem.name && this.movingItem.name.trim()
        ? this.movingItem.name
        : `(${String(this.movingItem.item_id).slice(0, 6)})`;
    },
  },
  mounted() {
    this.itemsArray = this.items;
  },
  watch: {
    items(val) {
      this.itemsArray = val;
    },
    itemsArray: {
      handler() {
        this.validateFields();
        this.$emit("update:items", this.itemsArray);
      },
      deep: true,
    },
  },
  methods: {
    getVisibleIndex(item) {
      return this.visibleFields.findIndex((f) => f.item_id === item.item_id);
    },

    onUpdate() {
      this.$emit("update:items", this.itemsArray);
    },

    onItemSelected(item) {
      this.itemSelected = item;
    },

    showModal(item) {
      this.itemSelected = item;
      this.$nextTick(() =>
        this.$root.$emit("bv::show::modal", "template_field_types_modal")
      );
    },

    onRemove(index) {
      if (confirm(this.$t("maintenance.confirm_delete_item"))) {
        const removed = this.itemsArray[index];
        this.itemsArray.splice(index, 1);

        if (removed && removed.item_id === this.movingItemId) {
          this.movingItemId = null;
        }

        this.$emit("update:items", this.itemsArray);
      }
    },

    validateFields() {
      if (this.itemsArray) {
        const errors = this.itemsArray.map((field) => {
          if (field.type === "facility") return false;

          if (!field.errors) field.errors = {};
          if (!field.hasErrors) field.hasErrors = false;

          field.errors.missingName = !field.name || field.name === "";
          field.errors.missingType = !field.type || field.type === "";
          field.errors.hasErrors =
            !field.name || field.name === "" || !field.type || field.type === "";

          return field.errors.hasErrors;
        });

        this.$emit("update:isValid", !errors.includes(true));
      }
    },

    onModalClose() {
      this.itemSelected = {};
    },

    startMove(item) {
      if (!item || !item.item_id) return;
      this.movingItemId = item.item_id;
    },

    cancelMove() {
      this.movingItemId = null;
    },

    canMoveHere(targetItem) {
      if (!this.moveMode || !targetItem) return false;
      return targetItem.item_id !== this.movingItemId;
    },

    moveItem(fromIndex, toIndex) {
      if (fromIndex < 0 || toIndex < 0) return;
      if (fromIndex === toIndex) return;

      const movedItem = this.itemsArray.splice(fromIndex, 1)[0];
      this.itemsArray.splice(toIndex, 0, movedItem);

      this.$emit("update:items", this.itemsArray);
    },

    moveBefore(targetItem) {
      if (!this.movingItemId || !targetItem) return;

      const fromIndex = this.itemsArray.findIndex((i) => i.item_id === this.movingItemId);
      const targetIndex = this.itemsArray.findIndex((i) => i.item_id === targetItem.item_id);

      if (fromIndex === -1 || targetIndex === -1) return;

      const adjustedTargetIndex = fromIndex < targetIndex ? targetIndex - 1 : targetIndex;
      this.moveItem(fromIndex, adjustedTargetIndex);
      this.cancelMove();
    },

    moveAfter(targetItem) {
      if (!this.movingItemId || !targetItem) return;

      const fromIndex = this.itemsArray.findIndex((i) => i.item_id === this.movingItemId);
      const targetIndex = this.itemsArray.findIndex((i) => i.item_id === targetItem.item_id);

      if (fromIndex === -1 || targetIndex === -1) return;

      const adjustedTargetIndex = fromIndex < targetIndex ? targetIndex : targetIndex + 1;
      this.moveItem(fromIndex, adjustedTargetIndex);
      this.cancelMove();
    },

    moveSelectedToTop() {
      if (!this.movingItemId) return;

      const fromIndex = this.itemsArray.findIndex((i) => i.item_id === this.movingItemId);
      if (fromIndex <= 0) {
        this.cancelMove();
        return;
      }

      this.moveItem(fromIndex, 0);
      this.cancelMove();
    },

    moveSelectedToBottom() {
      if (!this.movingItemId) return;

      const fromIndex = this.itemsArray.findIndex((i) => i.item_id === this.movingItemId);
      if (fromIndex === -1) return;

      const lastIndex = this.itemsArray.length - 1;
      if (fromIndex === lastIndex) {
        this.cancelMove();
        return;
      }

      this.moveItem(fromIndex, lastIndex);
      this.cancelMove();
    },
  },
};
</script>

<style lang="scss" scoped>
.template-builder {
  border: 1px solid #d1d1d1;

  .header {
    height: 33px;
    padding: 0 10px;
    background-color: #f4f4f4;
    border-bottom: 1px solid #d1d1d1;

    &-type-response {
      padding-left: 10px;
      width: calc(310px - 1px);
      height: 100%;
      line-height: 33px;
      border-left: 1px solid #d1d1d1;
    }
  }
}

.move-toolbar {
  padding: 8px 10px;
  background: #eef6ff;
  border-bottom: 1px solid #d1e7ff;
}
</style>
